/**
 * @file Context-driven notes UI for the Ordering hybrid modal.
 */

import {
    collection,
    doc,
    limit,
    onSnapshot,
    orderBy,
    query,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";
import { db } from "../../app/firebase/firebase.js";
import { saveTextFieldAndTagUsers } from "../../app/firebase/callables.js";
import { appData, state } from "../../app/state/state.js";
import { AUTO_GROWING_TEXTAREA_CLASSES } from "../../shared/forms/nativeFieldStyles.js";
import { initTagging, closeTaggingDropdown } from "../../shared/ui/tagging.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import {
    escapeHTML,
    formatAndSanitizeUserContent,
    formatContentForSave,
} from "../../shared/utils/contentUtils.js";
import {
    detachOrderingModalNoteListener,
    setOrderingModalNoteListener,
} from "./orderingListeners.js";

let activeItemId = null;
let activeNotes = [];
let activeNoteLimit = 100;
let activeNotesMayHaveMore = false;
let activeParentChange = null;
let activeDraftChange = null;
let activeNotesSession = null;
let notesSessionGeneration = 0;

function isCurrentNotesSession(session) {
    return Boolean(session &&
        activeNotesSession === session &&
        session.generation === notesSessionGeneration &&
        activeItemId === session.itemId &&
        session.slot.isConnected &&
        session.slot.dataset.itemId === session.itemId);
}

function acceptAuthoritativeParent(session, parent) {
    if (!isCurrentNotesSession(session) || !parent || session.itemId !== parent.id) {
        return;
    }
    activeParentChange?.(parent);
}

function authorLabel(uid) {
    const user = (appData.users || []).find((entry) => entry.id === uid);
    return user?.name || user?.email || "Unknown user";
}

function dateLabel(value) {
    const date = value?.toDate?.() ||
        (value instanceof Date ? value : null) ||
        ((value?.seconds ?? value?._seconds) ?
            new Date((value.seconds ?? value._seconds) * 1000) : null);
    return date ? date.toLocaleString("en-AU") : "Saving...";
}

function renderNotes(session = activeNotesSession) {
    if (!isCurrentNotesSession(session)) {return;}
    const slot = session.slot;
    const list = slot.querySelector("[data-ordering-note-list]");
    if (!list) {return;}
    list.innerHTML = activeNotes.length ? activeNotes.map((note) => `
        <article class="rounded-md border border-gray-200 bg-gray-50 p-2.5" data-ordering-note-id="${escapeHTML(note.id)}">
            <div class="text-sm leading-5 text-gray-700">${formatAndSanitizeUserContent(note.content || "")}</div>
            <p class="mt-1.5 text-[11px] text-gray-500">By ${escapeHTML(authorLabel(note.createdBy))} on ${escapeHTML(dateLabel(note.createdAt))}${note.id.startsWith("pending-") ? " - Saving..." : ""}</p>
        </article>
    `).join("") : "";
    const historyStatus = slot.querySelector("[data-ordering-note-history-status]");
    if (historyStatus) {
        historyStatus.textContent = activeNotesMayHaveMore ?
            `Showing the latest ${activeNoteLimit} notes.` : "";
        historyStatus.classList.toggle("hidden", !activeNotesMayHaveMore);
    }
    const loadMore = slot.querySelector("[data-ordering-note-load-more]");
    loadMore?.classList.toggle("hidden", !activeNotesMayHaveMore);
}

function attachOrderingNoteListeners(session) {
    if (!isCurrentNotesSession(session)) {return;}
    const itemId = session.itemId;
    const notesQuery = query(
        collection(db, "ordering_items", itemId, "notes"),
        orderBy("createdAt", "desc"),
        limit(activeNoteLimit),
    );
    const unsubscribeNotes = onSnapshot(notesQuery, (snapshot) => {
        if (!isCurrentNotesSession(session)) {return;}
        const durableIds = new Set(snapshot.docs.map((entry) => entry.id));
        const pending = activeNotes.filter((note) =>
            note.id.startsWith("pending-") &&
            (!note.durableNoteId || !durableIds.has(note.durableNoteId)));
        activeNotesMayHaveMore = snapshot.size === activeNoteLimit &&
            activeNoteLimit < 5000;
        activeNotes = [
            ...pending,
            ...snapshot.docs.map((entry) => ({id: entry.id, ...entry.data()})),
        ];
        renderNotes(session);
    }, (error) => {
        if (!isCurrentNotesSession(session)) {return;}
        console.error("Ordering note listener failed:", error);
    });
    const unsubscribeParent = onSnapshot(
        doc(db, "ordering_items", itemId),
        (snapshot) => {
            if (!isCurrentNotesSession(session) || !snapshot.exists()) {return;}
            acceptAuthoritativeParent(
                session,
                {id: snapshot.id, ...snapshot.data()},
            );
        },
        (error) => {
            if (isCurrentNotesSession(session)) {
                console.error("Ordering item listener failed:", error);
            }
        },
    );
    setOrderingModalNoteListener(() => {
        unsubscribeNotes();
        unsubscribeParent();
    });
}

function showNoteFailure(error) {
    openConfirmationModal(
        "Could not add note",
        escapeHTML(error?.message || "The note could not be saved."),
        async () => {},
        false,
        "OK",
        null,
        "",
        "",
        {hideCancel: true},
    );
}

/**
 * Mounts note history, tagging, and save behavior for one Ordering item.
 * @param {string} itemId Ordering item id.
 * @param {Function} [onParentChange] Authoritative parent listener callback.
 * @param {object} [saveCoordinator] Shared modal save coordinator.
 * @param {Function} [onDraftChange] Note draft dirty-state callback.
 * @returns {void}
 */
export function mountOrderingNotesModal(
    itemId,
    onParentChange,
    saveCoordinator,
    onDraftChange,
) {
    const slot = document.querySelector("[data-ordering-notes-slot]");
    if (!slot || !itemId) {return;}
    detachOrderingNotesModal();
    const session = {
        generation: notesSessionGeneration,
        itemId,
        slot,
    };
    activeNotesSession = session;
    activeItemId = itemId;
    activeNoteLimit = 100;
    activeNotesMayHaveMore = false;
    activeParentChange = typeof onParentChange === "function" ? onParentChange : null;
    activeDraftChange = typeof onDraftChange === "function" ? onDraftChange : null;
    slot.dataset.itemId = itemId;
    slot.innerHTML = `
        <h4 class="text-sm font-semibold text-gray-800">Notes</h4>
        <div class="mt-3" data-ordering-note-form>
            <textarea name="note" maxlength="20000" aria-invalid="false" class="${AUTO_GROWING_TEXTAREA_CLASSES}" placeholder="+ Add a new note"></textarea>
            <p class="mt-1 hidden text-xs font-medium text-red-600" data-ordering-note-error>Enter a note before saving.</p>
            <div class="mt-2 flex justify-end">
                <button type="button" class="rounded-md bg-[var(--dfc-brand-primary)] px-3 py-2 text-sm font-semibold text-[var(--dfc-brand-primary-contrast)] hover:bg-[var(--dfc-brand-primary-hover)] disabled:cursor-not-allowed disabled:opacity-50 sm:py-1.5">Save note</button>
            </div>
        </div>
        <div class="mt-4 max-h-64 space-y-2 overflow-y-auto" data-ordering-note-list role="log" aria-live="polite"></div>
        <div class="mt-2 flex items-center justify-between gap-3">
            <p class="hidden text-xs text-gray-500" data-ordering-note-history-status></p>
            <button type="button" class="hidden text-xs font-medium text-gray-600 hover:text-[var(--dfc-brand-primary)] focus:outline-none focus-visible:text-[var(--dfc-brand-primary)]" data-ordering-note-load-more>Load older notes</button>
        </div>
    `;
    const form = slot.querySelector("[data-ordering-note-form]");
    const textarea = form.querySelector('textarea[name="note"]');
    const submit = form.querySelector("button[type=button]");
    const noteError = form.querySelector("[data-ordering-note-error]");
    void initTagging(textarea, itemId, "ordering_items");
    saveCoordinator?.registerNoteSubmit?.(submit, textarea);
    const syncNoteDraftState = () => {
        if (!isCurrentNotesSession(session)) {return;}
        activeDraftChange?.(Boolean(textarea.value.trim()));
        if (textarea.value.trim()) {
            textarea.setAttribute("aria-invalid", "false");
            textarea.classList.remove(
                "border-red-600",
                "shadow-[0_0_0_1.5px_rgba(220,38,38,0.22)]",
            );
            noteError?.classList.add("hidden");
        }
        textarea.style.height = "auto";
        textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
    };
    textarea.addEventListener("input", syncNoteDraftState);
    textarea.addEventListener("change", syncNoteDraftState);
    form.addEventListener("input", (event) => {
        if (event.target === textarea) {syncNoteDraftState();}
    }, true);
    const saveNote = async () => {
        if (!isCurrentNotesSession(session)) {return;}
        const raw = textarea.value.trim();
        if (!raw) {
            textarea.setAttribute("aria-invalid", "true");
            textarea.classList.add(
                "border-red-600",
                "shadow-[0_0_0_1.5px_rgba(220,38,38,0.22)]",
            );
            noteError?.classList.remove("hidden");
            textarea.focus();
            return;
        }
        const releaseSave = saveCoordinator?.tryBegin?.("note");
        if (!releaseSave) {return;}
        if (!isCurrentNotesSession(session)) {
            releaseSave();
            return;
        }
        slot.setAttribute("aria-busy", "true");
        const pendingId = `pending-${crypto.randomUUID()}`;
        activeNotes.unshift({
            id: pendingId,
            content: raw,
            createdBy: state.currentUser?.uid,
            createdAt: new Date(),
        });
        renderNotes(session);
        textarea.value = "";
        textarea.dispatchEvent(new Event("input", {bubbles: true}));
        submit.disabled = true;
        submit.textContent = "Saving...";
        try {
            const savedContent = formatContentForSave(raw);
            const payload = {
                content: savedContent,
                parentId: itemId,
                parentType: "ordering_items",
                saveMode: "notesSubcollection",
            };
            const saveResult = await saveTextFieldAndTagUsers(payload);
            if (!isCurrentNotesSession(session)) {return;}
            const noteId = saveResult.data?.docId;
            if (!noteId) {throw new Error("The saved note identity was not returned.");}
            const pendingNote = activeNotes.find((note) => note.id === pendingId);
            if (pendingNote) {pendingNote.durableNoteId = noteId;}
            if (activeNotes.some((note) => note.id === noteId)) {
                activeNotes = activeNotes.filter((note) => note.id !== pendingId);
            }
        } catch (error) {
            if (!isCurrentNotesSession(session)) {return;}
            activeNotes = activeNotes.filter((note) => note.id !== pendingId);
            textarea.value = raw;
            textarea.dispatchEvent(new Event("input", {bubbles: true}));
            showNoteFailure(error);
        } finally {
            releaseSave();
            if (isCurrentNotesSession(session)) {
                submit.textContent = "Save note";
                slot.removeAttribute("aria-busy");
                renderNotes(session);
            }
        }
    };
    submit.addEventListener("click", () => void saveNote());
    textarea.addEventListener("keydown", (event) => {
        if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            void saveNote();
        }
    });
    slot.querySelector("[data-ordering-note-load-more]")?.addEventListener(
        "click",
        () => {
            if (!isCurrentNotesSession(session)) {return;}
            activeNoteLimit = Math.min(activeNoteLimit + 100, 5000);
            attachOrderingNoteListeners(session);
        },
    );
    attachOrderingNoteListeners(session);
}

/**
 * Detaches modal note state and tagging overlays.
 * @returns {void}
 */
export function detachOrderingNotesModal() {
    notesSessionGeneration += 1;
    activeNotesSession = null;
    activeItemId = null;
    activeNotes = [];
    activeParentChange = null;
    activeDraftChange = null;
    activeNotesMayHaveMore = false;
    closeTaggingDropdown();
    detachOrderingModalNoteListener();
}
